import { LiliTextInput } from '@lili/lili-frontend-common';
import { t } from 'i18next';
import { normalizeInputSpacesOnBlur, normalizeInputSpacesOnChange } from '@lili/utils/formatting';
import { useBoundStore } from '@Store/index';
import { ControllerWrapper } from '@Hooks/index';
import { OnboardingFormFields } from '@Types/index';
import { getRouteOrdinal, RoutesMap } from '@Router/router.types';
import { useBusinessAddressAnalytics } from '@Pages/happyFlowPages/businessAddress/BusinessAddressAnalytics.tsx';
import {
  AddressStateInput,
  useHandleOnCheckedChange,
} from '@Components/inputs/AddressStateDropDown';
import { ZipInput } from '@Components/inputs/ZipAddress';
import { CountryInput } from '@Components/inputs/AddressCountryAutoComplete/CountryInput';
import { Line1AddressInput } from '@Components/inputs/Line1Autocomplete/Line1AddressInput';
import { SameAddressCheckbox } from '@Components/inputs/SameAddressCheckbox/SameAddressCheckbox';
import type { ListItem } from '@lili/lili-frontend-common';
import type { Country } from '@Types/country';
import type { IBusinessAddressForm } from '@Pages/happyFlowPages/businessAddress/businessAddressForm/businessAddressSchema.tsx';
import type { ExtendedUseFormReturn } from '@Hooks/index';
interface IBusinessAddressFormProps {
  formMethods: ExtendedUseFormReturn<IBusinessAddressForm>;
  defaultValues: IBusinessAddressForm;
  countryList?: Country[] | undefined;
  states: ListItem[] | undefined;
  selectedCountry: Country | null | undefined;
  isUS: boolean;
}

export const BusinessAddressForm = (props: IBusinessAddressFormProps) => {
  const { reportActOnBusinessAddressScreen } = useBusinessAddressAnalytics();
  const {
    formMethods: { control, setValue, trigger, watch },
    formMethods,
    defaultValues,
    countryList,
    states,
    selectedCountry,
    isUS,
  } = props;
  const baseTabIndex = getRouteOrdinal(RoutesMap.BUSINESS_ADDRESS);
  const {
    customerData: { customerAddressData },
  } = useBoundStore((state) => state.customer);
  const { line1, line2, postalCode, state, city, country } = customerAddressData;
  const countryObject = countryList?.find((countryItem) => countryItem.code === country);
  const { handleOnCheckChange } = useHandleOnCheckedChange({
    setValue,
    state,
    line1,
    city,
    postalCode,
    line2,
    trigger,
    formMethods,
    defaultValues,
    country: countryObject,
    validStates: states,
  });
  const handleCheck = (isChecked: boolean) => {
    reportActOnBusinessAddressScreen(isChecked);
    handleOnCheckChange(isChecked);
  };

  const isChildrenOfCountryShouldBeDisabled = (countryList && !selectedCountry) || false;
  const hideStates = !isUS && !selectedCountry?.subDivisions?.length;
  const isSameAddress = watch(OnboardingFormFields.BUSINESS_ADDRESS_SAME_AS_PRIMARY);
  return (
    <div>
      <SameAddressCheckbox
        control={control}
        fieldName={OnboardingFormFields.BUSINESS_ADDRESS_SAME_AS_PRIMARY}
        label={t('pages.businessAddress.sameAsPersonal')}
        onChange={handleCheck}
      />
      <>
        <Line1AddressInput
          disabled={isSameAddress}
          tabIndex={baseTabIndex}
          isUsResident={isUS}
          validStates={states}
        />

        <div className='flex flex-col items-start w-full'>
          <div className='w-full'>
            <ControllerWrapper name={OnboardingFormFields.APARTMENT}>
              {({ value, onBlur, onChange, error }) => (
                <LiliTextInput
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                  id={OnboardingFormFields.APARTMENT}
                  title={t('pages.homeAddress.lineTwoFieldTitle')}
                  errorMessage={error?.message}
                  tabIndex={baseTabIndex + 1}
                  disabled={isSameAddress}
                  isPublic
                />
              )}
            </ControllerWrapper>
          </div>

          <div className='w-full'>
            <ControllerWrapper name={OnboardingFormFields.CITY}>
              {({ value, onBlur, onChange, error }) => (
                <LiliTextInput
                  value={value}
                  onBlur={() => {
                    onChange(normalizeInputSpacesOnBlur(value));
                    onBlur();
                  }}
                  onChange={(val) => onChange(normalizeInputSpacesOnChange(val))}
                  id={OnboardingFormFields.CITY}
                  title={t('pages.homeAddress.cityFieldTitle')}
                  errorMessage={error?.message}
                  disabled={isSameAddress}
                  tabIndex={baseTabIndex + 2}
                  isPublic
                />
              )}
            </ControllerWrapper>
          </div>

          {!hideStates && (
            <div className='w-full' data-testid='country_input_wrapper'>
              {countryList && (
                <CountryInput
                  tabIndex={baseTabIndex + 3}
                  disabled={isSameAddress}
                  countryList={countryList as unknown as ListItem[]}
                />
              )}
            </div>
          )}
        </div>

        <div className='w-full flex sm:flex-row flex-col sm:gap-[16px]'>
          {hideStates && (
            <div className='flex-1' data-testid='country_input_wrapper'>
              {countryList && (
                <CountryInput
                  tabIndex={baseTabIndex + 3}
                  disabled={isSameAddress}
                  countryList={countryList as unknown as ListItem[]}
                />
              )}
            </div>
          )}

          {!hideStates && (
            <div className='flex-1' data-testid='state_dropdown_wrapper'>
              <AddressStateInput
                disabled={isSameAddress || isChildrenOfCountryShouldBeDisabled}
                tabIndex={baseTabIndex + 4}
                statesList={states}
              />
            </div>
          )}

          <div className='flex-1'>
            <ZipInput
              disabled={isSameAddress || isChildrenOfCountryShouldBeDisabled}
              tabIndex={baseTabIndex + 5}
              isUS={isUS}
            />
          </div>
        </div>
      </>
    </div>
  );
};
